import {call, put} from '@redux-saga/core/effects';
import api from 'api';
import ApiService from 'api/ApiService';
import {DialogResult, DialogTypes} from 'common/names';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {pushErrorToast, pushSuccessToast} from 'features/notifications/redux/actions';
import i18n from 'i18n';
import {getPassword} from 'redux/common/saga/getPassword';
import {asyncSagaConstructor, unhandedSagaError} from 'redux/common';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {Action} from 'types/redux';
import {Transport} from 'types/transport/Abstract/AbstractTransport';
import {keyTypes} from '..';
import {
   escapeFileName,
   getFileNameFromPath,
   getFileNameFromPathWithoutExtension,
} from 'common/stringUtils';
import {getName} from 'redux/common/saga/getName';
import {getTransportSchemaByType} from 'types/transport/Transport';
import {castToSend} from 'common/yup/castToSend';

const PUBLIC_TITLE = 'Import OpenPGP Public Key To';
const SECRET_TITLE = 'Import OpenPGP Key Pair To';
const SSH_PUBLIC_TITLE = 'Import SSH Client Public Key To';
const SSH_SECRET_TITLE = 'Import SSH Secret Key To';

const ASC_EXT = '.asc';
// const PGP_EXT = '.pgp';

const ASC_EXT_MATCHER = '*.asc';
const PGP_EXT_MATCHER = '*.pgp';

// const ASC_TYPE = 'ASCII Armored (*.asc)';
// const PGP_TYPE = 'OpenPGP (*.pgp)';
// const FILE_TYPES = {ASC_TYPE, PGP_TYPE};

const PUBLIC_EXT = '.pub';
const PUBLIC_EXT_MATCHER = '*.pub';
const ALL_EXT_MATCHER = '*.*';

// const PUBLIC_TYPE = 'Public SSH Key (*.pub)';
// const SECRET_TYPE = 'SSH Key Pair (*.*)';
// const SSH_PUBLIC_FILE_TYPES = {PUBLIC_TYPE};
// const SSH_SECRET_FILE_TYPES = {SECRET_TYPE};

type IHandler = {
   title: string;
   errTitle: string;
   buildFilename: (s: string) => string;
   filesOfType: string[];
   url: string;
   // schema: ObjectSchema<any>;
};

const handlers: Record<string, IHandler> = {
   public: {
      title: PUBLIC_TITLE,
      errTitle: i18n.t('Error Importing Public Key'),
      buildFilename: (name: string) => name + ASC_EXT,
      filesOfType: [ASC_EXT_MATCHER, PGP_EXT_MATCHER],
      url: api.importRemotePgpPublic,
      // schema: ExportPgpPublicPayload,
   },
   secret: {
      title: SECRET_TITLE,
      errTitle: i18n.t('Error Importing Key Pair'),
      buildFilename: (name: string) => name + ASC_EXT,
      filesOfType: [ASC_EXT_MATCHER, PGP_EXT_MATCHER],
      url: api.importRemotePgpSecret,
      // schema: ExportPgpSecretPayload,
   },
   publicSsh: {
      title: SSH_PUBLIC_TITLE,
      errTitle: i18n.t('Error Importing SSH Public Key'),
      buildFilename: (name: string) => name + PUBLIC_EXT,
      filesOfType: [PUBLIC_EXT_MATCHER],
      url: api.importRemoteSshPublic,
      // schema: ExportSshPublicPayload,
   },
   secretSsh: {
      title: SSH_SECRET_TITLE,
      errTitle: i18n.t('Error Importing SSH Key Pair'),
      buildFilename: (name: string) => name,
      filesOfType: [ALL_EXT_MATCHER],
      url: api.importRemoteSshSecret,
      // schema: ExportSshSecretPayload,
   },
};

export type ImportRemoteKeyPayload = {
   filePath: string;
   transport: Transport;
   partnerId: string;
   secret?: boolean;
   ssh?: boolean;
   setLoading: (state: boolean) => void;
};

export function* importRemoteKey(action: Action<ImportRemoteKeyPayload>) {
   const {filePath, secret, ssh, transport: _transport, partnerId, setLoading} = action.payload;
   let handler = handlers.public;
   if (ssh) {
      handler = secret ? handlers.secretSsh : handlers.publicSsh;
   } else {
      handler = secret ? handlers.secret : handlers.public;
   }
   const {errTitle, url} = handler;
   let passphrase: string;
   let keyName = getFileNameFromPathWithoutExtension(filePath);

   const schema = getTransportSchemaByType(_transport.transportType);
   const transport = castToSend(schema, _transport);

   try {
      setLoading(true);
      if (secret) {
         passphrase = yield call(
            getPassword,
            i18n.t('Passphrase'),
            i18n.t('Enter File Passphrase')
         );
         if (!passphrase) {
            return;
         }
      }

      keyName = yield call(getName, i18n.t('Set the key name'), i18n.t('Name'), keyName);

      if (!keyName) return;

      yield call(
         asyncSagaConstructor(url, keyTypes.IMPORT_REMOTE_KEY, {
            getMethod: ApiService.postSecureJson,
            data: {
               // asciiArmored: filePath?.indexOf(ASC_EXT) > 0,
               path: filePath,
               keyName,
               overwrite: false,
               // passphrase: passphrase!,
               transport,
               partnerId,
            },
         })
      );

      yield put(
         pushSuccessToast(`Key ${filePath} successfully imported to
      ${keyName}`)
      );
   } catch (err) {
      // const e = yield err.clone().text();
      console.error(err);
      const e = JSON.stringify(err);
      if (e.indexOf('File already exists') >= 0) {
         // TODO, need ability to give a different name
         yield call(overwriteFlow, url, filePath, transport, partnerId, passphrase!, keyName);
      } else {
         yield put(pushErrorToast(errTitle));
      }
      //TODO
   } finally {
      setLoading(false);
   }
}

function* overwriteFlow(
   url: string,
   // schema: ObjectSchema<any>,
   filePath: string,
   transport: Transport,
   partnerId: string,
   passphrase?: string,
   keyName?: string
) {
   try {
      const dialogResult: ICloseSubDialogAction<null> = yield call(openDialogWithResult, {
         id: DialogTypes.ConfirmationDialog.id,
         title: i18n.t('Key File Already Exists'),
         content: i18n.t('Do you want to replace it?'),
      });

      const shouldOverwrite = dialogResult.payload?.result === DialogResult.OK;

      if (shouldOverwrite) {
         yield call(
            asyncSagaConstructor(url, keyTypes.IMPORT_REMOTE_KEY, {
               getMethod: ApiService.postSecureJson,
               data: {
                  // asciiArmored: filePath?.indexOf(ASC_EXT) > 0,
                  path: filePath,
                  keyName,
                  overwrite: true,
                  // passphrase,
                  transport,
                  partnerId,
               },
            })
         );
         yield put(
            pushSuccessToast(`Key ${filePath} successfully imported to
      ${keyName}`)
         );
      }
   } catch (e) {
      yield call(unhandedSagaError, e as Error);
   }
}
